import { LegalPage } from './LegalPage'

export function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="June 7, 2026">
      <p>
        This Privacy Policy describes how TailwindX, LLC ("Company", "we", "us") collects, uses, and protects information when you use Dr. Scribey ("Service").
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Account Information</h3>
      <ul>
        <li>Name, email address, specialty, practice size, and current EMR system</li>
        <li>Authentication credentials (hashed — we never store plaintext passwords)</li>
      </ul>

      <h3>Clinical Data (Protected Health Information)</h3>
      <ul>
        <li><strong>Audio recordings:</strong> Streamed in real-time for transcription. Audio is never stored on our servers or on your device.</li>
        <li><strong>Transcripts:</strong> Text output from audio transcription, stored encrypted at rest.</li>
        <li><strong>Clinical notes:</strong> AI-generated documentation, stored encrypted at rest.</li>
        <li><strong>Patient demographics:</strong> Names, dates of birth, MRNs as entered by the provider.</li>
      </ul>

      <h3>Usage Data</h3>
      <ul>
        <li>Feature usage patterns, session duration, error logs</li>
        <li>No third-party analytics or tracking pixels</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li><strong>Transcription:</strong> Audio is streamed in real-time for speech-to-text conversion.</li>
        <li><strong>Note generation:</strong> Transcripts are processed by AI to generate clinical documentation.</li>
        <li><strong>Personalization:</strong> Your note style preferences and templates are used to customize output to your documentation style.</li>
        <li><strong>Coding suggestions:</strong> Transcripts and notes are analyzed to suggest ICD-10 and CPT codes.</li>
      </ul>

      <h2>3. PHI Tokenization</h2>
      <p>
        Before any transcript is sent to our AI models for note generation, <strong>patient-identifying information is replaced with anonymous tokens</strong>. The AI never processes real patient names, dates of birth, or medical record numbers. Tokens are rehydrated only after processing, within your encrypted session.
      </p>

      <h2>4. Data Storage and Security</h2>
      <ul>
        <li><strong>Location:</strong> All data is processed and stored in the United States.</li>
        <li><strong>Encryption in transit:</strong> All connections are encrypted.</li>
        <li><strong>Encryption at rest:</strong> All stored data is encrypted using industry-standard methods.</li>
        <li><strong>Access controls:</strong> Role-based access with automatic session timeout.</li>
        <li><strong>Audit logging:</strong> Immutable logs of all data access and modifications.</li>
      </ul>
      <p>
        For full security details, see our <a href="/security" className="text-indigo-600 hover:text-indigo-700">Security page</a>.
      </p>

      <h2>5. What We Do NOT Do</h2>
      <ul>
        <li>❌ We do <strong>not sell</strong> your data. Ever.</li>
        <li>❌ We do <strong>not use</strong> your clinical data to train AI models.</li>
        <li>❌ We do <strong>not share</strong> your data with third parties for marketing.</li>
        <li>❌ We do <strong>not store</strong> audio recordings.</li>
        <li>❌ We do <strong>not use</strong> third-party analytics or tracking pixels.</li>
      </ul>

      <h2>6. Third-Party Services</h2>
      <p>
        We use a limited number of HIPAA-compliant service providers to operate Dr. Scribey. All providers maintain Business Associate Agreements (BAAs) and are contractually prohibited from retaining or using your data beyond the scope of their services. A current list of providers is available upon request.
      </p>

      <h2>7. Data Retention</h2>
      <ul>
        <li>Clinical data (notes, transcripts, patient records) is retained for the duration of your subscription.</li>
        <li>Upon account cancellation, you may request data export within 30 days.</li>
        <li>After 30 days post-cancellation, all data is permanently deleted.</li>
        <li>Audit logs are retained for 7 years as required by healthcare regulations.</li>
      </ul>

      <h2>8. Your Rights</h2>
      <ul>
        <li><strong>Access:</strong> You can access all your data at any time through the Service.</li>
        <li><strong>Export:</strong> Download your notes and patient data in standard formats.</li>
        <li><strong>Deletion:</strong> Request complete deletion of your account and all associated data.</li>
        <li><strong>Correction:</strong> Edit or correct any information at any time.</li>
      </ul>

      <h2>9. California Privacy Rights (CCPA)</h2>
      <p>
        If you are a California resident, you have the right to: know what personal information we collect, request deletion of your personal information, and opt out of the sale of personal information (we do not sell personal information). To exercise these rights, contact us at the email below.
      </p>

      <h2>10. Children's Privacy</h2>
      <p>
        Dr. Scribey is not intended for use by individuals under 18. We do not knowingly collect personal information from children.
      </p>

      <h2>11. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be communicated via email or in-app notification at least 30 days before taking effect.
      </p>

      <h2>12. Contact</h2>
      <p>
        Privacy questions? Contact us at{' '}
        <a href="mailto:info@tailwindx.org" className="text-indigo-600 hover:text-indigo-700">info@tailwindx.org</a>.
      </p>
    </LegalPage>
  )
}
